import { useQuery, QueryStatus } from "@tanstack/react-query";
import { Shield } from "lucide-react";
import { Fragment } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { getScoreBarColor } from "@/lib/chart";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import type { CreativesScorecardData, SubBrandComplianceItem } from "@/schema/brand-os/brand-governance";

function SubBrand({ item }: { item: SubBrandComplianceItem }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground/70">{item.name}</span>
        <span className="text-xs font-medium text-foreground/40">{item.score}%</span>
      </div>
      <Progress value={item.score} className="h-2 w-full bg-muted" indicatorClassName={getScoreBarColor(item.score)} />
    </div>
  );
}

function SubBrandContent({ data }: { data: CreativesScorecardData }) {
  const items = (data.sub_brand_overview ?? []).map((item, index) => ({
    id: item.sub_brand ?? String(index),
    name: item.sub_brand,
    score: item.score ?? 0,
  }));

  return items.map((item) => <SubBrand key={item.id} item={item} />);
}

function SubBrandComponent({ status, data }: { status: QueryStatus; data?: CreativesScorecardData }) {
  switch (status) {
    case "pending":
      return <SubBrandSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data && data.sub_brand_overview?.length ? <SubBrandContent data={data} /> : <LockedContent message={message} />;
  }
}

export function SubBrandCompliance({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <Card className="col-span-1 lg:col-span-2 gap-3">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
            <Shield className="size-5 text-muted-foreground" />
          </div>
          <CardTitle>Sub-Brand Compliance</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col space-y-4">
        <SubBrandComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function SubBrandSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex items-center justify-between mb-1">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-4 w-10 rounded-md" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </Fragment>
  );
}
